/**
 * useCodeEditorFiles Hook
 *
 * Manages file operations for the Code Editor view.
 * Uses the Files API to load COBOL (read-only) and Java (read/write) files.
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { useApp } from '../context'

/**
 * Hook for Code Editor file operations
 *
 * @param {Object} application - The current application object
 * @param {Object} account - The account object (optional, will be fetched if not provided)
 * @param {string} source - File source: 'analysis' (default) or 'refactor'
 * @param {string} ingestJobId - Ingest job ID for COBOL file loading
 * @param {string} javaJobId - Code Analysis or Code Refactor job ID for Java files
 * @param {string} sectionsJobId - Code Analysis job ID for sections (ALWAYS code_analysis)
 * @param {string} preFlightJobId - Pre-Flight job ID for compile failure Java (optional)
 * @param {string} javaPackageJobId - Java Packaging job ID for package compile failure Java (optional)
 * @param {string} editorScope - 'focused' (default) or 'full' — controls which Java files to list
 * @param {string} upstreamJobId - For full scope: svcgen or jval job ID to list all Java files
 * @param {string} upstreamJobType - 'svcgen' | 'jval' — type of upstream job
 */
export function useCodeEditorFiles(application, account = null, source = 'analysis', ingestJobId = null, javaJobId = null, sectionsJobId = null, preFlightJobId = null, javaPackageJobId = null, editorScope = 'focused', upstreamJobId = null, upstreamJobType = null) {
  const { db, settings } = useApp()

  // Determine if we're loading refactored files
  const isRefactorSource = source === 'refactor'
  const isCompileSource = source === 'compile'
  const isPackageSource = source === 'package'

  // Get API base URL from settings (loaded from database)
  const apiBaseUrl = useMemo(() => {
    const current = settings.getCurrent()
    return current.apiBaseUrl || 'http://localhost:8000'
  }, [settings])

  // State
  const [accountData, setAccountData] = useState(account)
  const [cobolFiles, setCobolFiles] = useState([])
  const [javaFiles, setJavaFiles] = useState([])
  const [sections, setSections] = useState([])
  const [workspaceStatus, setWorkspaceStatus] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // Derive account_id and app_name for API calls
  // Use scoutAccountId from application (preferred) or account
  const apiContext = useMemo(() => {
    if (import.meta.env.DEV) console.log('[useCodeEditorFiles] Building apiContext:', { application, accountData })
    if (!application) {
      if (import.meta.env.DEV) console.log('[useCodeEditorFiles] No application')
      return null
    }
    // Application has scoutAccountId directly, or fall back to accountData
    const accountId = application.scoutAccountId || accountData?.scoutAccountId
    if (import.meta.env.DEV) console.log('[useCodeEditorFiles] accountId:', accountId, 'app name:', application.name)
    if (!accountId) {
      if (import.meta.env.DEV) console.log('[useCodeEditorFiles] No accountId found')
      return null
    }
    const ctx = {
      account: accountId,
      app: application.name
    }
    if (import.meta.env.DEV) console.log('[useCodeEditorFiles] apiContext ready:', ctx)
    return ctx
  }, [accountData, application])

  // Fetch account data if not provided
  useEffect(() => {
    const fetchAccount = async () => {
      if (!application?.account_id || account) return

      try {
        const acc = await db.getAccount(application.account_id)
        setAccountData(acc)
      } catch (err) {
        console.error('Failed to fetch account:', err)
        setError('Failed to load account data')
      }
    }

    fetchAccount()
  }, [application?.account_id, account, db])

  // Build API URL with optional parameters
  const buildUrl = useCallback((endpoint, includeSource = false, includeIngestJobId = false, includeJavaJobId = false, includeSectionsJobId = false) => {
    if (!apiContext) return null
    let url = `${apiBaseUrl}/files/${apiContext.account}/${apiContext.app}${endpoint}`
    // Add source param for java endpoints when using refactor source
    if (includeSource && isRefactorSource) {
      url += (url.includes('?') ? '&' : '?') + 'source=refactor'
    }
    // Add ingest_job_id for COBOL endpoints
    if (includeIngestJobId && ingestJobId) {
      url += (url.includes('?') ? '&' : '?') + `ingest_job_id=${encodeURIComponent(ingestJobId)}`
    }
    // 2026-02-10: Add java_job_id for Java files endpoint
    if (includeJavaJobId && javaJobId) {
      url += (url.includes('?') ? '&' : '?') + `job_id=${encodeURIComponent(javaJobId)}`
    }
    // 2026-02-10: Add sections_job_id for Sections endpoint (ALWAYS code_analysis)
    if (includeSectionsJobId && sectionsJobId) {
      url += (url.includes('?') ? '&' : '?') + `job_id=${encodeURIComponent(sectionsJobId)}`
    }
    return url
  }, [apiContext, apiBaseUrl, isRefactorSource, ingestJobId, javaJobId, sectionsJobId])

  // Fetch JSON from API with Authorization header
  const fetchJson = useCallback(async (endpoint) => {
    const url = buildUrl(endpoint)
    if (!url) throw new Error('API context not ready')

    // 2026-02-09: Add Authorization header (consistent with jobPoller.js)
    const token = localStorage.getItem('modernizeit_token')
    const headers = {}
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    const response = await fetch(url, { headers })
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: response.statusText }))
      throw new Error(error.detail || `API error: ${response.status}`)
    }
    return response.json()
  }, [buildUrl])

  // Load file lists using NEW bundle endpoint (Option D: Job-scoped contract)
  const loadFileLists = useCallback(async () => {
    if (import.meta.env.DEV) {
      console.log(` [useCodeEditorFiles] loadFileLists — scope=${editorScope} upstream=${upstreamJobId}`)
      console.log('[useCodeEditorFiles] Job IDs: ingest=', ingestJobId, 'analysis=', sectionsJobId, 'refactor=', javaJobId)
      console.log(` [useCodeEditorFiles] pre_flight_job_id=${preFlightJobId} java_package_job_id=${javaPackageJobId}`)
    }

    if (!apiContext) return

    // 2026-02-11: Option D - Require explicit job IDs, no fallbacks
    if (!ingestJobId) {
      setError('Missing ingest_job_id. Run Upload Files first.')
      return
    }
    if (!sectionsJobId) {
      setError('Missing code_analysis_job_id. Run Code Analysis first.')
      return
    }

    setLoading(true)
    setError(null)

    try {
      console.log('[useCodeEditorFiles] Fetching bundle from API...')

      // 2026-02-11: Get auth token (required!)
      const token = localStorage.getItem('modernizeit_token')
      const headers = {}
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }

      // 2026-02-11: Build bundle endpoint URL
      // Required: ingest_job_id, code_analysis_job_id
      // Optional: code_refactor_job_id (for refactored Java)
      const params = new URLSearchParams({
        ingest_job_id: ingestJobId,
        code_analysis_job_id: sectionsJobId  // Sections ALWAYS from code_analysis
      })

      // If viewing refactor editor, pass code_refactor_job_id for Java
      // If viewing service-generation editor, pass service_generation_job_id for Java
      // If viewing compile editor, pass pre_flight_job_id for compile failure Java
      // If viewing package editor, pass java_package_job_id for package compile failure Java
      const isServiceGenSource = source === 'service-generation'
      if (isPackageSource && javaPackageJobId) {
        params.append('java_package_job_id', javaPackageJobId)
      } else if (isCompileSource && preFlightJobId) {
        params.append('pre_flight_job_id', preFlightJobId)
      } else if (isRefactorSource && javaJobId) {
        params.append('code_refactor_job_id', javaJobId)
      } else if (isServiceGenSource && javaJobId) {
        params.append('service_generation_job_id', javaJobId)
      }

      // Full-scope: add editor_scope + upstream job ID when in 'full' mode
      if (editorScope === 'full' && upstreamJobId) {
        params.append('editor_scope', 'full')
        if (upstreamJobType === 'svcgen') {
          params.append('upstream_svcgen_job_id', upstreamJobId)
        } else {
          params.append('upstream_jval_job_id', upstreamJobId)
        }
      }

      const bundleUrl = `${apiBaseUrl}/editor/bundle?${params.toString()}`

      if (import.meta.env.DEV) {
        console.log(' [CODE_EDITOR] Bundle URL:', bundleUrl, `scope=${editorScope}`)
        console.log(' [CODE_EDITOR] Auth header present:', !!headers['Authorization'])
      }

      // Fetch bundle
      const response = await fetch(bundleUrl, { headers })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: response.statusText }))
        throw new Error(`Editor bundle failed (${response.status}): ${errorData.detail || response.statusText}`)
      }

      const bundle = await response.json()
      if (import.meta.env.DEV) {
        const javaCount = bundle.java?.files?.length || 0
        console.log(` [useCodeEditorFiles] Bundle received: cobol=${bundle.cobol?.files?.length || 0} java=${javaCount} sections=${bundle.sections?.data?.sections?.length || 0} scope=${editorScope}`)
        if (isCompileSource || editorScope === 'full') {
          console.log(' [COMPILE_BUNDLE] S3 prefix used:', bundle.java?.base_uri)
          console.log(` [COMPILE_BUNDLE] Java files loaded: ${javaCount} (scope=${editorScope})`)
          if (javaCount > 0) console.log(' [COMPILE_BUNDLE] First 10 filenames:', (bundle.java.files).slice(0, 10).map(f => f.name))
        }
      }

      // 2026-02-11: Transform bundle response to expected format
      // COBOL files from bundle.cobol
      const cobolList = (bundle.cobol?.files || []).map(f => ({
        id: f.name.toLowerCase().replace(/\.[^.]+$/, ''),
        name: f.name,
        path: f.path,
        type: 'cobol',
        job_id: f.job_id  // For job-scoped content loading
      }))

      // Java files from bundle.java
      const javaList = (bundle.java?.files || []).map(f => ({
        id: f.name.toLowerCase().replace(/\.[^.]+$/, ''),
        name: f.name,
        path: f.path,
        type: 'java',
        job_id: f.job_id,  // For job-scoped content loading
        stage: f.stage      // 'analysis' or 'refactor'
      }))

      // Sections from bundle.sections.data
      const sectionsData = bundle.sections?.data || {}
      const sectionsList = (sectionsData.sections || []).map((s, index) => ({
        id: `section-${index}`,
        name: s.cobol_name,
        displayName: s.cobol_name,
        startLine: s.cobol_start_line,
        endLine: s.cobol_end_line,
        type: 'paragraph',
        status: s.status === 'converted' ? 'converted' : 'pending',
        javaMethod: s.java_method,
        javaClass: s.java_class
      }))

      setCobolFiles(cobolList)
      setJavaFiles(javaList)
      setSections(sectionsList)

      // Still fetch workspace status separately (not part of bundle)
      const statusRes = await fetchJson('/workspace/status').catch((e) => {
        console.error('Status fetch error:', e)
        return null
      })
      setWorkspaceStatus(statusRes)

    } catch (err) {
      console.error('Failed to load files:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [apiContext, fetchJson, buildUrl, isCompileSource, isPackageSource, preFlightJobId, javaPackageJobId, editorScope, upstreamJobId, upstreamJobType])

  // Load files when context is ready or source/scope changes
  useEffect(() => {
    if (apiContext) {
      loadFileLists()
    }
  }, [apiContext, isRefactorSource, isCompileSource, isPackageSource, preFlightJobId, javaPackageJobId, editorScope, upstreamJobId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Read COBOL file content using job-scoped endpoint
  const readCobolFile = useCallback(async (filePath, fileMetadata = null) => {
    if (!apiContext) throw new Error('API context not ready')

    // 2026-02-11: Use job-scoped /editor/cobol/file endpoint
    // fileMetadata should contain { job_id, path } from bundle response
    const jobId = fileMetadata?.job_id || ingestJobId
    const relativePath = fileMetadata?.path || filePath

    if (!jobId) {
      throw new Error('Ingest job ID not available. Run Upload Files first.')
    }

    const token = localStorage.getItem('modernizeit_token')
    const headers = {}
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    // NEW: Job-scoped endpoint
    const url = `${apiBaseUrl}/editor/cobol/file?job_id=${encodeURIComponent(jobId)}&path=${encodeURIComponent(relativePath)}`
    if (import.meta.env.DEV) console.log(' [READ_COBOL] Calling:', url)

    const response = await fetch(url, { headers })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: response.statusText }))
      throw new Error(error.detail || 'Failed to read COBOL file')
    }

    const data = await response.json()
    return data.content
  }, [apiContext, ingestJobId, apiBaseUrl])

  // Read Java file content using job-scoped endpoint
  const readJavaFile = useCallback(async (filePath, fileMetadata = null) => {
    if (!apiContext) throw new Error('API context not ready')

    // 2026-02-11: Use job-scoped /editor/java/file endpoint
    // fileMetadata should contain { job_id, path } from bundle response
    const jobId = fileMetadata?.job_id || javaJobId
    const relativePath = fileMetadata?.path || filePath

    if (!jobId) {
      throw new Error('Java file job_id not available. Run Code Analysis or Code Refactor first.')
    }

    const token = localStorage.getItem('modernizeit_token')
    const headers = {}
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    // NEW: Job-scoped endpoint
    let url = `${apiBaseUrl}/editor/java/file?job_id=${encodeURIComponent(jobId)}&path=${encodeURIComponent(relativePath)}`
    // Full-scope: add editor_scope + gate_job_id so backend can check java_source_full/ overlays
    if (editorScope === 'full') {
      url += `&editor_scope=full`
      const gateJobId = isPackageSource ? javaPackageJobId : preFlightJobId
      if (gateJobId) url += `&gate_job_id=${encodeURIComponent(gateJobId)}`
    }
    if (import.meta.env.DEV) {
      console.log(' [READ_JAVA] Calling:', url)
      if (isCompileSource || editorScope === 'full') {
        console.log(' [COMPILE_READ] Requesting file:', relativePath, 'from job:', jobId, `scope=${editorScope}`)
      }
    }

    const response = await fetch(url, { headers })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: response.statusText }))
      throw new Error(error.detail || 'Failed to read Java file')
    }

    const data = await response.json()
    return data.content
  }, [apiContext, javaJobId, apiBaseUrl, editorScope, isPackageSource, javaPackageJobId, preFlightJobId, isCompileSource])

  // Save Java file content using new job-scoped /editor/file endpoint
  const saveJavaFile = useCallback(async (filePath, content) => {
    if (!apiContext) throw new Error('API context not ready')

    // 2026-02-11: Validate required job IDs
    if (!ingestJobId) {
      throw new Error('Missing ingest_job_id. Cannot save without job context.')
    }
    if (!sectionsJobId) {
      throw new Error('Missing code_analysis_job_id. Cannot save without job context.')
    }

    // 2026-02-11: Add Authorization header
    const token = localStorage.getItem('modernizeit_token')
    const headers = {
      'Content-Type': 'application/json'
    }
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    // 2026-02-11: NEW job-scoped endpoint - saves to {artifacts_path}/user_edits/
    const url = `${apiBaseUrl}/editor/file`

    // Build request body with job IDs
    const requestBody = {
      ingest_job_id: ingestJobId,
      code_analysis_job_id: sectionsJobId,
      relative_path: filePath,
      content: content
    }

    // If viewing refactor editor, include code_refactor_job_id
    // If viewing service-generation editor, include service_generation_job_id
    // If viewing compile editor, include pre_flight_job_id
    // If viewing package editor, include java_package_job_id
    const isServiceGenSource = source === 'service-generation'
    if (isPackageSource && javaPackageJobId) {
      requestBody.java_package_job_id = javaPackageJobId
    } else if (isCompileSource && preFlightJobId) {
      requestBody.pre_flight_job_id = preFlightJobId
    } else if (isRefactorSource && javaJobId) {
      requestBody.code_refactor_job_id = javaJobId
    } else if (isServiceGenSource && javaJobId) {
      requestBody.service_generation_job_id = javaJobId
    }

    // Full-scope: tell backend to use java_source_full/ overlay prefix
    requestBody.editor_scope = editorScope

    if (import.meta.env.DEV) {
      console.log(' [SAVE_JAVA] Calling:', url)
      console.log(' [SAVE_JAVA] scope:', requestBody.editor_scope, 'path:', requestBody.relative_path, 'length:', content.length)
    }

    const response = await fetch(url, {
      method: 'PUT',
      headers,
      body: JSON.stringify(requestBody)
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: response.statusText }))
      throw new Error(error.detail || 'Failed to save Java file')
    }

    const result = await response.json()
    if (import.meta.env.DEV) console.log(' [SAVE_JAVA] File saved successfully:', result.uri)
    return result
  }, [apiContext, apiBaseUrl, ingestJobId, sectionsJobId, javaJobId, isRefactorSource, isCompileSource, isPackageSource, preFlightJobId, javaPackageJobId, editorScope])

  // Delete Java file overlay (Start Fresh) - resets to original generated/refactored code
  const deleteJavaFileOverlay = useCallback(async (filePath) => {
    if (!apiContext) throw new Error('API context not ready')

    // 2026-02-11: Validate required job IDs
    if (!ingestJobId) {
      throw new Error('Missing ingest_job_id. Cannot delete overlay without job context.')
    }
    if (!sectionsJobId) {
      throw new Error('Missing code_analysis_job_id. Cannot delete overlay without job context.')
    }

    // 2026-02-11: Add Authorization header
    const token = localStorage.getItem('modernizeit_token')
    const headers = {
      'Content-Type': 'application/json'
    }
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    // 2026-02-11: DELETE job-scoped overlay endpoint
    const url = `${apiBaseUrl}/editor/file`

    // Build request body with job IDs (same as PUT, but content is ignored)
    const requestBody = {
      ingest_job_id: ingestJobId,
      code_analysis_job_id: sectionsJobId,
      relative_path: filePath,
      content: ''  // Required by Pydantic model but not used for DELETE
    }

    // If viewing refactor editor, include code_refactor_job_id
    // If viewing service-generation editor, include service_generation_job_id
    // If viewing compile editor, include pre_flight_job_id
    // If viewing package editor, include java_package_job_id
    const isServiceGenSourceDel = source === 'service-generation'
    if (isPackageSource && javaPackageJobId) {
      requestBody.java_package_job_id = javaPackageJobId
    } else if (isCompileSource && preFlightJobId) {
      requestBody.pre_flight_job_id = preFlightJobId
    } else if (isRefactorSource && javaJobId) {
      requestBody.code_refactor_job_id = javaJobId
    } else if (isServiceGenSourceDel && javaJobId) {
      requestBody.service_generation_job_id = javaJobId
    }

    // Full-scope: tell backend to delete from java_source_full/ overlay prefix
    requestBody.editor_scope = editorScope

    if (import.meta.env.DEV) {
      console.log(' [DELETE_OVERLAY] Calling:', url, 'path:', requestBody.relative_path)
    }

    const response = await fetch(url, {
      method: 'DELETE',
      headers,
      body: JSON.stringify(requestBody)
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: response.statusText }))
      throw new Error(error.detail || 'Failed to delete overlay')
    }

    const result = await response.json()
    if (import.meta.env.DEV) console.log(' [DELETE_OVERLAY] Result:', result.message, 'deleted:', result.deleted)
    return result
  }, [apiContext, apiBaseUrl, ingestJobId, sectionsJobId, javaJobId, isRefactorSource, isCompileSource, isPackageSource, preFlightJobId, javaPackageJobId, editorScope])

  // Get project files structure (combined COBOL and Java)
  const projectFiles = useMemo(() => ({
    cobol: cobolFiles,
    java: javaFiles
  }), [cobolFiles, javaFiles])

  // Check if ready (has context and files loaded)
  const isReady = useMemo(() => {
    return !!apiContext && !loading
  }, [apiContext, loading])

  return {
    // State
    projectFiles,
    cobolFiles,
    javaFiles,
    sections,
    workspaceStatus,
    loading,
    listLoading: loading,   // alias: true while bundle fetch is in-flight (same state)
    error,
    isReady,

    // API context (for debugging)
    apiContext,

    // Actions
    loadFileLists,
    readCobolFile,
    readJavaFile,
    saveJavaFile,
    deleteJavaFileOverlay,  // Start Fresh - delete overlay, reset to original
  }
}

export default useCodeEditorFiles
